import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { KeenIcon } from '@/components';
import { Can } from '@casl/react';
import { useAbility } from '@/providers/';
import { useAuthContext } from '@/auth';
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import DataTable from 'datatables.net-react';
import DT from 'datatables.net-dt';
import 'datatables.net-select-dt';
import 'datatables.net-responsive-dt';
import { createRoot } from 'react-dom/client';
import { createDataTableOptions, createDataTableConfig, getAccessToken } from '@/utils';
import { renderAssignees } from './renderAssignees';
import { ConfirmationModal } from '@/components/modal';
import { useConfirmationModal } from '@/hooks';
import { SelectInput, FilterContainer } from '@/components/TableFilter';
import reminderService from '@/services/reminder/reminderService';
import { ReminderModal } from './ReminderModal';
import { TaskTitle } from './SourceBadge';

DataTable.use(DT);

const STATUS_OPTIONS = [
  { label: 'Pending', value: 'pending' },
  { label: 'Completed', value: 'completed' },
];

/**
 * Reminders list for a contact or matter detail page. One component for both —
 * only the filter key differs.
 */
const RemindersTab = ({ parentType, parentId }) => {
  const { logout } = useAuthContext();
  const { ability } = useAbility();
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState('create');
  const [selectedData, setSelectedData] = useState(null);
  const [itemId, setItemId] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showFilter, setShowFilter] = useState(false);
  const { isOpen, actionId, openModal, closeModal } = useConfirmationModal();

  const [filterData, setFilterData] = useState({
    [`${parentType}_id`]: parentId,
    status: null,
  });

  const token = getAccessToken();
  const tableRef = useRef(null);
  const filterRef = useRef(filterData);
  const formikRef = useRef();

  const canManage = ability.can('handle', 'reminder');
  const canDelete = ability.can('delete', 'reminder');

  useEffect(() => {
    filterRef.current = filterData;
  }, [filterData]);

  const columns = [
    {
      title: 'Title',
      name: 'title',
      data: 'title',
      render: function (value, type, row) {
        return type === 'display' ? `<div id="reminder-title-${row.id}"></div>` : value;
      },
    },
    { title: 'Due', name: 'due_at', data: 'due_at' },
    {
      title: 'Status',
      name: 'status',
      data: 'status',
      render: function (value, type, row) {
        // is_overdue is computed server-side — due_at is a display-formatted
        // string and cannot be parsed reliably here.
        const isOverdue = row.is_overdue;
        const badgeClass = value === 'completed'
          ? 'badge-success'
          : isOverdue ? 'badge-danger' : 'badge-warning';
        const label = isOverdue ? 'overdue' : value;
        return `<span class="badge badge-pill badge-outline capitalize ${badgeClass}">${label}</span>`;
      },
    },
    {
      title: 'Assigned To',
      name: 'assigned_to',
      data: null,
      searchable: false,
      render: function (data, type, row) {
        return `<div id="reminder-assigned-${row.id}"></div>`;
      },
    },
    {
      title: 'Notes',
      name: 'notes',
      data: 'notes',
      render: function (value) {
        if (!value) return '-';
        return value.length > 60 ? `${value.slice(0, 60)}…` : value;
      },
    },
    {
      title: '',
      data: null,
      orderable: false,
      searchable: false,
      render: function (data, type, row) {
        return `<div id="reminder-action-${row.id}"></div>`;
      },
    },
  ];

  const refresh = () => {
    tableRef.current?.dt()?.ajax.reload();
  };

  const handleSubmit = async (formData) => {
    const isEdit = mode === 'edit';
    const action = isEdit ? reminderService.updateReminder : reminderService.createReminder;
    const args = isEdit ? [formData, itemId] : [formData];
    try {
      isEdit ? setLoading(true) : setIsLoading(true);
      const { status } = await action(...args);
      if (status) {
        handleModalClose();
        refresh();
        toast.success(`Task ${isEdit ? 'Updated' : 'Created'} Successfully`, {
          description: `Task ${isEdit ? 'updated' : 'created'} successfully.`,
        });
      }
    } catch (error) {
      console.error('Error', error);
      if (error.message == '422 Unprocessable Entity. Validation failed.') {
        const formattedErrors = Object.fromEntries(
          Object.entries(error.errors).map(([key, value]) => [key, value[0]])
        );
        formikRef.current?.setErrors(formattedErrors);
        toast.error('Validation Error.', {
          description: 'Please check your input. Some fields may be invalid.',
        });
        return;
      }
      toast.error('Oops! Something went wrong.', {
        description: 'An error occurred while saving the task. Please try again in a moment.',
      });
    } finally {
      isEdit ? setLoading(false) : setIsLoading(false);
    }
  };

  const handleEdit = async (id) => {
    setItemId(id);
    setMode('edit');
    setOpen(true);
    try {
      const { data, status } = await reminderService.getSingleReminder(id);
      if (status) {
        setSelectedData(data);
      }
    } catch (error) {
      console.error('Error', error);
      toast.error('Failed to fetch Task.', {
        description: 'An error occurred while fetching the task. Please try again later.',
      });
    }
  };

  const handleToggleStatus = async (row) => {
    const isCompleted = row.status === 'completed';
    try {
      const { status } = isCompleted
        ? await reminderService.reopenReminder(row.id)
        : await reminderService.completeReminder(row.id);
      if (status) {
        refresh();
        toast.success(`Task ${isCompleted ? 'Reopened' : 'Completed'}`, {
          description: `Task ${isCompleted ? 'reopened' : 'marked complete'} successfully.`,
        });
      }
    } catch (error) {
      console.error('Error', error);
      toast.error('Failed to update Task.', {
        description: 'An error occurred while updating the task. Please try again later.',
      });
    }
  };

  const handleDelete = async () => {
    closeModal();
    try {
      const { status } = await reminderService.deleteReminder(actionId);
      if (status) {
        refresh();
        toast.success('Deleted Task.', {
          description: 'Task deleted Successfully.',
        });
      }
    } catch (error) {
      console.error('Error', error);
      toast.error('Failed to delete Task.', {
        description: 'An error occurred while deleting the task. Please try again later.',
      });
    }
  };

  const handleModalClose = () => {
    setItemId(null);
    setSelectedData(null);
    setMode('create');
    setOpen(false);
  };

  const renderCustomActions = (row) => (
    <DropdownMenu>
      {(canManage || canDelete) && (
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="icon" className="text-gray-600">
            <KeenIcon icon="dots-vertical" className="text-[18px]" />
          </Button>
        </DropdownMenuTrigger>
      )}
      <DropdownMenuContent align="end" className="w-44">
        <Can I="handle" a="reminder" ability={ability}>
          <DropdownMenuItem onClick={() => handleEdit(row.id)}>
            <KeenIcon icon="notepad-edit" className="mr-0.5 h-4 w-4 cursor-pointer" /> Edit
          </DropdownMenuItem>
        </Can>
        <Can I="handle" a="reminder" ability={ability}>
          <DropdownMenuItem onClick={() => handleToggleStatus(row)}>
            <KeenIcon icon="check-circle" className="mr-0.5 h-4 w-4 cursor-pointer" />
            {row.status === 'completed' ? 'Reopen' : 'Mark Complete'}
          </DropdownMenuItem>
        </Can>
        <Can I="delete" a="reminder" ability={ability}>
          <DropdownMenuItem onClick={() => openModal(row.id)} className="text-red-600 cusror-pointer">
            <KeenIcon icon="trash" className="mr-0.5 h-4 w-4" /> Delete
          </DropdownMenuItem>
        </Can>
      </DropdownMenuContent>
    </DropdownMenu>
  );

  const ajaxConfig = createDataTableConfig('/reminders/data_table_list', filterRef, token, logout);

  const options = createDataTableOptions({
    order: [[1, 'asc']],
    drawCallback: function () {
      const api = this.api();
      api.rows({ page: 'current' }).data().each((row) => {
        const actionContainer = document.getElementById(`reminder-action-${row.id}`);
        if (actionContainer) {
          if (!actionContainer._reactRoot) {
            actionContainer._reactRoot = createRoot(actionContainer);
          }
          actionContainer._reactRoot.render(renderCustomActions(row));
        }
        const assignedContainer = document.getElementById(`reminder-assigned-${row.id}`);
        if (assignedContainer) {
          if (!assignedContainer._reactRoot) {
            assignedContainer._reactRoot = createRoot(assignedContainer);
          }
          assignedContainer._reactRoot.render(renderAssignees(row.assigned_to));
        }
        const titleContainer = document.getElementById(`reminder-title-${row.id}`);
        if (titleContainer) {
          if (!titleContainer._reactRoot) {
            titleContainer._reactRoot = createRoot(titleContainer);
          }
          titleContainer._reactRoot.render(<TaskTitle reminder={row} />);
        }
      });
    },
  });

  const handleFilterChange = (key, val) => {
    setFilterData((prev) => {
      const updated = { ...prev, [key]: val };
      filterRef.current = updated;
      return updated;
    });
  };

  const resetFilters = () => {
    const cleared = { [`${parentType}_id`]: parentId, status: null };
    setFilterData(cleared);
    filterRef.current = cleared;
    refresh();
  };

  return (
    <>
      <FilterContainer show={showFilter} onApply={refresh} onReset={resetFilters}>
        <SelectInput
          label="Status"
          value={filterData.status}
          onChange={(val) => handleFilterChange('status', val)}
          staticOptions={STATUS_OPTIONS}
          customClass="sm:w-[32%] w-full"
        />
      </FilterContainer>

      <div className="min-w-full mb-4 card">
        <div className="card-header">
          <h3 className="card-title">Tasks</h3>

          <div className="flex flex-wrap items-center gap-2">
            <button className="text-xs btn btn-sm btn-primary btn-outline" onClick={() => setShowFilter((prev) => !prev)}>
              <KeenIcon icon="filter" className="w-fit btn-icon" />
              {showFilter ? 'Hide Advanced Filters' : 'Advanced Filters'}
            </button>
            <Can I="handle" a="reminder" ability={ability}>
              <button className="text-xs btn btn-sm btn-light" onClick={() => setOpen(true)}>
                <KeenIcon icon="plus" className="w-fit btn-icon" />
                Add New
              </button>
            </Can>
          </div>
        </div>

        <DataTable
          id={`reminders-${parentType}-tab`}
          ajax={ajaxConfig}
          columns={columns}
          options={options}
          ref={tableRef}
        />

        <ReminderModal
          open={open}
          onClose={handleModalClose}
          mode={mode}
          data={selectedData}
          onSubmit={handleSubmit}
          parentType={parentType}
          parentId={parentId}
          createLoading={isLoading}
          updateLoading={loading}
          ref={formikRef}
        />

        <ConfirmationModal
          show={isOpen}
          onClose={closeModal}
          onConfirm={handleDelete}
          message="Are you sure you want to delete this task? This action is permanent and cannot be undone."
          title="Are You Sure?"
        />
      </div>
    </>
  );
};

export { RemindersTab };
